/**
 * Single source of truth for RepTree's password policy on the client. These rules MIRROR the
 * Supabase Auth settings (Dashboard, Authentication, Sign In / Providers): a minimum length plus
 * at least one lowercase letter, uppercase letter, digit and symbol. Keep them in lockstep with
 * that config so the sign-up and reset screens never accept a password the server will reject.
 *
 * "Prevent use of leaked passwords" (HIBP) can only be verified server side, so it is NOT a live
 * checklist item; it is surfaced from the auth error instead (see friendlyAuthError).
 */

// Supabase's "symbols" group is the full set of printable ASCII punctuation (no whitespace, no
// Unicode). This string is exactly that set, so a password that passes our symbol check also
// satisfies Supabase's.
const SYMBOLS = "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~"

export const PASSWORD_MIN_LENGTH = 8

export type PasswordRuleId = 'length' | 'lower' | 'upper' | 'digit' | 'symbol'

export interface PasswordRule {
  id: PasswordRuleId
  label: string
  met: boolean
}

/** Evaluate every deterministic rule against a candidate password, in the order we display them. */
export function passwordRequirements(pw: string): PasswordRule[] {
  return [
    { id: 'length', label: `At least ${PASSWORD_MIN_LENGTH} characters`, met: pw.length >= PASSWORD_MIN_LENGTH },
    { id: 'lower', label: 'A lowercase letter', met: /[a-z]/.test(pw) },
    { id: 'upper', label: 'An uppercase letter', met: /[A-Z]/.test(pw) },
    { id: 'digit', label: 'A number', met: /[0-9]/.test(pw) },
    { id: 'symbol', label: 'A symbol', met: [...pw].some((c) => SYMBOLS.includes(c)) },
  ]
}

/** True when the password satisfies every deterministic rule (HIBP is checked by Supabase). */
export function passwordMeetsPolicy(pw: string): boolean {
  return passwordRequirements(pw).every((r) => r.met)
}

/**
 * Turn a Supabase auth error into copy a person can act on. A leaked or weak password (the checks
 * the live checklist cannot run) gets a plain explanation; anything else falls back to the raw
 * message. Returns '' when there is no error.
 */
export function friendlyAuthError(error: unknown): string {
  if (!error) return ''
  const e = error as { message?: string; code?: string; name?: string; reasons?: unknown }
  const reasons = Array.isArray(e.reasons) ? (e.reasons as string[]) : []
  const message = e.message ?? ''
  if (reasons.includes('pwned') || /pwned|breach|leaked|compromis/i.test(message)) {
    return 'This password has shown up in a known data breach. Please choose a different one.'
  }
  if (reasons.length > 0 || e.code === 'weak_password' || e.name === 'AuthWeakPasswordError') {
    return 'That password does not meet the requirements below. Please strengthen it.'
  }
  // Email confirmation / one-time code flows.
  if (e.code === 'otp_expired' || /otp.*expired|token has expired|invalid.*(otp|token)/i.test(message)) {
    return 'That code has expired or was already used. Send a new one.'
  }
  if (e.code === 'over_email_send_rate_limit' || /rate limit|only request this after|too many/i.test(message)) {
    return 'Please wait a minute, then try again.'
  }
  if (e.code === 'captcha_failed' || /captcha/i.test(message)) {
    return "The security check did not finish. Wait a moment for it to complete, then try again."
  }
  if (e.code === 'email_not_confirmed' || /email not confirmed/i.test(message)) {
    return 'Confirm your email first: enter the code we sent you.'
  }
  return message || 'Something went wrong. Please try again.'
}
